import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FileText, MessageSquare, Folder, Wrench, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    blogs: 0,
    messages: 0,
    projects: 0,
    tools: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogsRes, messagesRes, projectsRes, toolsRes] = await Promise.all([
          axios.get(`${API}/blogs?published_only=false`, { headers: getAuthHeader() }),
          axios.get(`${API}/contact`, { headers: getAuthHeader() }),
          axios.get(`${API}/projects`),
          axios.get(`${API}/tools`)
        ]);

        setStats({
          blogs: blogsRes.data.length,
          messages: messagesRes.data.length,
          projects: projectsRes.data.length,
          tools: toolsRes.data.length
        });

        setRecentMessages(messagesRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeader]);

  const statCards = [
    { name: 'Articles', value: stats.blogs, icon: FileText, color: 'bg-blue-500', link: '/admin/blogs' },
    { name: 'Messages', value: stats.messages, icon: MessageSquare, color: 'bg-green-500', link: '/admin/messages' },
    { name: 'Projets', value: stats.projects, icon: Folder, color: 'bg-purple-500', link: null },
    { name: 'Outils', value: stats.tools, icon: Wrench, color: 'bg-orange-500', link: null },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1e3a5f]">
            Tableau de Bord
          </h1>
          <p className="text-gray-500 mt-1">Bienvenue dans l'administration G Division</p>
        </div>
        <Link to="/admin/blogs/new">
          <Button 
            className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none"
            data-testid="new-blog-btn"
          >
            <Plus className="mr-2" size={18} />
            Nouvel Article
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div 
            key={index}
            className="stats-card bg-white p-6 shadow-sm"
            data-testid={`stat-card-${stat.name.toLowerCase()}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm uppercase tracking-wider">{stat.name}</p>
                <p className="text-3xl font-bold text-[#1e3a5f] mt-2">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-sm flex items-center justify-center`}>
                <stat.icon className="text-white" size={24} />
              </div>
            </div>
            {stat.link && (
              <Link 
                to={stat.link}
                className="block mt-4 text-sm text-[#c9a227] hover:underline"
              >
                Voir tout →
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Recent Messages */}
      <div className="bg-white shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#1e3a5f]">
            Messages Récents
          </h2>
          <Link to="/admin/messages" className="text-[#c9a227] text-sm hover:underline">
            Voir tous →
          </Link>
        </div>

        {recentMessages.length > 0 ? (
          <div className="space-y-4">
            {recentMessages.map((message, index) => (
              <div 
                key={message.id}
                className="flex items-start gap-4 p-4 bg-[#f8f9fa] border-l-4 border-[#c9a227]"
                data-testid={`recent-message-${index}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#1e3a5f]">{message.name}</span>
                    <span className="text-gray-400 text-xs">•</span>
                    <span className="text-gray-400 text-xs">{formatDate(message.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">{message.subject}</p>
                  <p className="text-sm text-gray-500 truncate">{message.message}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Aucun message pour le moment.</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
